import { Id } from "@dhx/ts-common/types";
import { DataCollection } from "../datacollection";
import { dhxError, dhxWarning, isEqualObj, toDataDriver, hasJsonOrArrayStructure } from "../helpers";
import { DataEvents, IDataChangeStack, IDataDriver, IDataProxy, DataDriver } from "../types";

export class Loader {
	private _parent: DataCollection;
	private _saving: boolean;
	private _changes: any;

	constructor(parent: DataCollection, changes: any) {
		this._parent = parent;
		this._changes = changes; // todo: [dirty] mutation
	}

	load(url: IDataProxy, driver?: IDataDriver | DataDriver): Promise<any> {
		// TODO: change way for checking lazyLoad
		if (url.config && !this._parent.events.fire(DataEvents.beforeLazyLoad, [])) {
			return;
		}
		return (this._parent.loadData = url
			.load()
			.then(data => {
				if (data) {
					return this.parse(data, driver);
				} else {
					return [];
				}
			})
			.catch(error => {
				this._parent.events.fire(DataEvents.loadError, [error]);
			}));
	}

	parse(data: any | any[], driver: IDataDriver | DataDriver = DataDriver.json) {
		if (driver === "json" && !hasJsonOrArrayStructure(data)) {
			this._parent.events.fire(DataEvents.loadError, ["Uncaught SyntaxError: Unexpected end of input"]);
		}
		driver = toDataDriver(driver);
		data = driver.toJsonArray(data);
		if (!(data instanceof Array)) {
			const totalCount = data.total_count - 1;
			const from = data.from;
			data = data.data;
			if (this._parent.getLength() === 0) {
				const newData = [];
				for (let i = 0, j = 0; i <= totalCount; i++) {
					if (i >= from && i <= from + data.length - 1) {
						newData.push(data[j]);
						j++;
					} else {
						newData.push({ $empty: true });
					}
				}
				data = newData;
			} else {
				data.forEach((newItem, i) => {
					const index = from + i;
					const oldId = this._parent.getId(index);
					if (oldId) {
						const emptyItem = this._parent.getItem(oldId);
						if (emptyItem && emptyItem.$empty) {
							this._parent.changeId(oldId, newItem.id, true);
							this._parent.update(newItem.id, { ...newItem, $empty: undefined }, true);
						}
					} else {
						dhxWarning("item not found");
					}
				});
				this._parent.events.fire(DataEvents.afterLazyLoad, [from, data.length]);
				this._parent.events.fire(DataEvents.change);
				return data;
			}
		}
		if (this._parent.getInitialData()) {
			this._parent.removeAll();
		}
		this._parent.$parse(data);
		return data;
	}

	save(url: IDataProxy) {
		const uniqueChanges = this._getUniqueOrder();
		for (const el of uniqueChanges) {
			if (el.saving || el.pending) {
				dhxWarning("item is saving");
			} else {
				const prevEl = this._findPrevState(el.id);

				if (prevEl && prevEl.saving) {
					const pending = new Promise((res, rej) => {
						prevEl.promise
							.then(() => {
								el.pending = false;
								res(this._setPromise(el, url));
							})
							.catch(err => {
								this._removeFromOrder(prevEl);
								this._setPromise(el, url);
								dhxWarning(err);
								rej(err);
							});
					});
					this._addToChain(pending);
					el.pending = true;
				} else {
					this._setPromise(el, url);
				}
			}
		}
		if (uniqueChanges.length) {
			this._parent.saveData.then(() => {
				this._saving = false;
			});
		}
	}

	updateChanges(changes: IDataChangeStack) {
		this._changes = changes;
	}
	private _setPromise(el, url): Promise<any> {
		let status;
		switch (el.status) {
			case "remove":
				status = "delete";
				break;
			case "add":
				status = "insert";
				break;
			default:
				status = el.status;
				break;
		}
		el.promise = url.save(el.obj, status);
		el.promise
			.then(() => {
				this._removeFromOrder(el);
			})
			.catch(err => {
				el.saving = false;
				el.error = true;
				dhxError(err);
			});
		el.saving = true;
		this._saving = true;
		this._addToChain(el.promise);
		return el.promise;
	}

	private _addToChain(promise): void {
		// eslint-disable-next-line @typescript-eslint/no-misused-promises
		if (this._parent.saveData && this._saving) {
			this._parent.saveData = this._parent.saveData.then(() => promise);
		} else {
			this._parent.saveData = promise;
		}
	}

	private _findPrevState(id: Id): any {
		for (const el of this._changes.order) {
			if (el.id === id) {
				return el;
			}
		}
		return null;
	}

	private _removeFromOrder(el) {
		this._changes.order = this._changes.order.filter(item => !isEqualObj(item, el));
	}

	private _getUniqueOrder() {
		return this._changes.order.reduce((unique, el) => {
			const ind = unique.findIndex(item => item.id === el.id);
			const involvedElem = ind > -1 ? unique[ind] : null;
			if (involvedElem && involvedElem.saving === false && involvedElem.status === "add") {
				if (el.status === "remove") {
					unique.splice(ind, 1);
				} else {
					involvedElem.obj = el.obj;
				}
			} else if (involvedElem && involvedElem.saving === false && involvedElem.status === "update") {
				unique.splice(ind, 1, el);
			} else {
				unique.push(el);
			}
			return unique;
		}, []);
	}
}
